import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { listPlans, startCheckout } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Features in order of what parents care about most.
const PREMIUM_PERKS = [
  "Full GCSE curriculum, Maths, Biology and Chemistry",
  "Mock exams and adaptive quizzes",
  "Parent dashboard and weekly digests",
  "Personalised revision from your notes",
  "Priority AI tutor",
  "Unlimited AI podcasts",
  "AQA, Edexcel and OCR exam boards",
];
const FREE_PERKS = [
  "3 free topics per subject",
  "AI revision books and flashcards",
  "Basic quiz mode",
];
const FAMILY_PERKS = [
  "Everything in Premium, for up to 3 children",
  "Each child gets their own profile and progress",
  "One parent dashboard for all your children",
  "Family leaderboard",
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    listPlans()
      .then(setPlans)
      .catch(() => toast.error("Failed to load plans"))
      .finally(() => setLoading(false));
  }, []);

  const subscribe = async (lookup_key) => {
    if (!user) return navigate("/login");
    setCheckingOut(lookup_key);
    try {
      const { checkout_url } = await startCheckout(lookup_key);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setCheckingOut(null);
    }
  };

  const filtered = useMemo(
    () => plans.filter((p) => (period === "monthly" ? p.interval === "month" : p.interval === "year")),
    [plans, period]
  );

  // Monthly premium → used for Family-plan savings math.
  const premiumMonthly = plans.find((p) => p.lookup_key === "premium_monthly");
  const premiumMonthlyAmount = premiumMonthly?.amount || 0;

  const yearlySavings = useMemo(() => {
    const pm = plans.find((p) => p.lookup_key === "premium_monthly");
    const py = plans.find((p) => p.lookup_key === "premium_yearly");
    if (!pm || !py || pm.amount === 0) return null;
    const annualIfMonthly = pm.amount * 12;
    return Math.round(100 - (py.amount / annualIfMonthly) * 100);
  }, [plans]);

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950 p-6 sm:p-10">
      <main className="max-w-[960px] mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 nb-border rounded-full px-4 py-1.5 bg-[#FFE066] mb-6">
            <Sparkles className="w-4 h-4" strokeWidth={3} aria-hidden="true" />
            <span className="label-caps text-black">Built for GCSE</span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mb-3">
            Less stress.{" "}
            <span className="bg-[#9DFFB0] dark:bg-[#68D391] px-3 nb-border rounded-2xl inline-block">
              Better grades.
            </span>
          </h1>
          <p className="text-base font-medium text-muted max-w-2xl mx-auto">
            Cancel anytime. 7-day free trial on every plan. Maths, Biology and Chemistry across AQA, Edexcel and OCR.
          </p>
        </div>

        {/* Period switch, same lavender toggle style used elsewhere */}
        <div role="radiogroup" aria-label="Billing period" className="flex justify-center mb-8">
          <div className="nb-border rounded-full bg-white dark:bg-zinc-900 p-1 flex gap-1">
            {[
              { v: "monthly", l: "Monthly" },
              { v: "yearly", l: yearlySavings ? `Yearly, save ${yearlySavings}%` : "Yearly" },
            ].map((o) => {
              const selected = period === o.v;
              return (
                <button
                  key={o.v}
                  role="radio"
                  aria-checked={selected}
                  data-testid={`period-${o.v}`}
                  onClick={() => setPeriod(o.v)}
                  className={`px-5 py-2 rounded-full font-bold text-sm transition-all flex items-center gap-1.5 ${
                    selected
                      ? "bg-[#D4C4FB] dark:bg-[#B794F4] text-black"
                      : "hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  }`}
                >
                  {selected && <Check className="w-3.5 h-3.5" aria-hidden="true" strokeWidth={3} />}
                  {o.l}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="text-center font-bold">Loading plans…</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Free */}
            <div className="nb-card p-7 flex flex-col">
              <div className="label-caps mb-2">Free</div>
              <div className="font-display text-5xl font-black mb-3 tabular-nums">£0</div>
              <p className="text-muted font-medium mb-5 text-sm">
                Try ReviseAI with no card. Great for a quick taste.
              </p>
              <ul className="space-y-2 mb-6 flex-1">
                {FREE_PERKS.map((p) => (
                  <li key={p} className="flex gap-2 text-sm font-medium">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={3} aria-hidden="true" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate(user ? "/" : "/login")}
                data-testid="plan-free"
                className="nb-button px-5 py-3 w-full text-sm"
              >
                {user ? "Stay on Free" : "Get Started"}
              </button>
            </div>

            {filtered.map((p) => {
              const isPremium = p.lookup_key.startsWith("premium");
              const isFamily = p.lookup_key.startsWith("family");
              const perks = isFamily ? FAMILY_PERKS : PREMIUM_PERKS;
              const perMonth = p.interval === "year" ? (p.amount / 12).toFixed(2) : null;
              return (
                <div
                  key={p.lookup_key}
                  data-testid={`plan-${p.lookup_key}`}
                  className={`nb-card p-7 flex flex-col relative ${isPremium ? "bg-[#EFFFF3] dark:bg-[#1a3326]" : ""}`}
                >
                  {isPremium && (
                    <div className="absolute -top-3 right-6 nb-border rounded-full px-3 py-1 bg-[#FFE066] text-black label-caps">
                      Most popular
                    </div>
                  )}
                  <div className="label-caps mb-2">{p.name.split(" · ")[0]}</div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <div className="font-display text-5xl font-black tabular-nums">
                      £{p.amount.toFixed(p.amount % 1 === 0 ? 0 : 2)}
                    </div>
                    <div className="font-bold text-sm">/{p.interval}</div>
                  </div>
                  {perMonth && (
                    <div className="text-xs font-bold text-muted mb-3 tabular-nums">
                      That's £{perMonth}/month
                    </div>
                  )}
                  <p className="font-medium text-sm mb-2 text-muted">
                    {isFamily
                      ? "For up to 3 children. One price for the whole family."
                      : "Everything one student needs for GCSE revision."}
                  </p>
                  {isFamily && premiumMonthlyAmount > 0 && (
                    <p className="text-xs font-bold text-muted mb-3 tabular-nums">
                      2 children on Premium would be £{(premiumMonthlyAmount * 2).toFixed(2)}/month. 3 would be £{(premiumMonthlyAmount * 3).toFixed(2)}.
                    </p>
                  )}
                  <ul className="space-y-2 mb-6 flex-1 mt-3">
                    {perks.map((perk) => (
                      <li key={perk} className="flex gap-2 text-sm font-medium">
                        <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={3} aria-hidden="true" />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => subscribe(p.lookup_key)}
                    data-testid={`subscribe-${p.lookup_key}`}
                    disabled={checkingOut === p.lookup_key}
                    className={`${isPremium ? "nb-button-primary" : "nb-button"} px-5 py-3 w-full flex items-center justify-center gap-2 text-sm`}
                  >
                    {checkingOut === p.lookup_key ? "Loading…" : "Start Free Trial"}
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <p className="text-xs font-medium text-muted mt-3 leading-snug">
                    Card required. We'll email you 2 days before your trial ends. Cancel anytime in Settings.
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-10 text-center text-sm text-muted font-medium">
          Questions written to the AQA, Edexcel and OCR GCSE specifications.
        </p>
        <p className="mt-2 text-center text-sm text-muted font-medium">
          Cancel anytime. 7-day free trial. All plans billed in GBP.
        </p>
      </main>
    </div>
  );
}
